import { useEffect, useRef, useState } from 'react'
import { getConfig, trackEvent } from '../../lib/config'

import styles from './Hcaptcha.module.scss'

declare global {
  interface Window {
    hcaptcha?: {
      render: (
        container: HTMLElement,
        params: {
          sitekey: string
          theme?: string
          callback?: () => void
          'expired-callback'?: () => void
          'error-callback'?: (error?: unknown) => void
        },
      ) => string
      getResponse: (widgetId: string) => string
      reset: (widgetId: string) => void
    }
  }
}

const SCRIPT_SRC = 'https://js.hcaptcha.com/1/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (window.hcaptcha) {
    return Promise.resolve()
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('hCaptcha failed to load'))
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export type HcaptchaHandle = { getResponse: () => string; reset: () => void }

export function isCaptchaRequired(): boolean {
  const config = getConfig()
  return config.hcaptchaEnabled && Boolean(config.hcaptchaSiteKey || config.devCaptchaResponse)
}

type HcaptchaProps = {
  theme?: string
  handleRef?: React.MutableRefObject<HcaptchaHandle | null>
  form: 'sign_in' | 'sign_up' | 'confirmation' | 'contact'
  onResponseChange?: (hasResponse: boolean) => void
}

export function Hcaptcha(props: HcaptchaProps) {
  const { hcaptchaEnabled, devCaptchaResponse } = getConfig()
  if (hcaptchaEnabled && devCaptchaResponse) {
    return <DevCaptcha response={devCaptchaResponse} {...props} />
  }
  return <HcaptchaWidget {...props} />
}

function DevCaptcha({
  response,
  handleRef,
  onResponseChange,
}: HcaptchaProps & { response: string }) {
  const [checked, setChecked] = useState(false)

  const check = (next: boolean) => {
    setChecked(next)
    onResponseChange?.(next)
  }

  useEffect(() => {
    if (!handleRef) {
      return
    }
    handleRef.current = { getResponse: () => (checked ? response : ''), reset: () => check(false) }
    return () => {
      handleRef.current = null
    }
  })

  return (
    <label className={styles.devCaptcha}>
      <input type="checkbox" checked={checked} onChange={(event) => check(event.target.checked)} />
      No soy un robot (captcha de desarrollo)
    </label>
  )
}

function HcaptchaWidget({ theme = 'light', handleRef, form, onResponseChange }: HcaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const config = getConfig()

  useEffect(() => {
    if (!isCaptchaRequired()) {
      return
    }
    let widgetId: string | null = null
    let cancelled = false
    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.hcaptcha) {
        return
      }
      containerRef.current.innerHTML = ''
      widgetId = window.hcaptcha.render(containerRef.current, {
        sitekey: config.hcaptchaSiteKey as string,
        theme,
        callback: () => onResponseChange?.(true),
        'expired-callback': () => onResponseChange?.(false),
        'error-callback': (error) => trackEvent('Captcha Failed', { form, reason: 'widget_error', error: String(error ?? '') }),
      })
      if (handleRef) {
        handleRef.current = {
          getResponse: () => (widgetId !== null ? (window.hcaptcha?.getResponse(widgetId) ?? '') : ''),
          // hCaptcha fires no callback on reset, so the widget reports it itself.
          reset: () => {
            if (widgetId !== null) {
              window.hcaptcha?.reset(widgetId)
            }
            onResponseChange?.(false)
          },
        }
      }
    }
    void loadScript().then(renderWidget, () => trackEvent('Captcha Failed', { form, reason: 'script_load' }))
    return () => {
      cancelled = true
      if (handleRef) {
        handleRef.current = null
      }
    }
  }, [config.hcaptchaEnabled, config.hcaptchaSiteKey, theme, handleRef, form, onResponseChange])

  if (!isCaptchaRequired()) {
    return null
  }
  return <div ref={containerRef} className="h-captcha" />
}
